import { useCallback, useEffect, useState } from 'react'
import useIsMounted from 'ismounted'
import { ArticleClient, TimelineEventFilter } from 'api/clients/ArticleClient'
import { ArticleService } from 'api/services/ArticleService'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { FetchRequestState } from 'types'
import { Timeline, TimelineEvent } from 'screens/Activity/Types'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useArticleEventsRealtimeChannel } from './useArticleEventsRealtimeChannel'
import produce from 'immer'
import { useQueryClient } from 'react-query'

const MIN_EVENTS_FIRST_PAGE_SIZE = 5

export const useArticleTimeline = (
  articleKey: string,
  options: {
    userId: string | undefined
    receiveRealtimeUpdates: boolean
    skip: boolean | undefined
  } = {
    userId: undefined,
    receiveRealtimeUpdates: false,
    skip: false,
  },
  layerId?: string,
  initialFilters?: TimelineEventFilter[],
  timestamp?: string,
) => {
  const client = useQueryClient()
  const { userId, receiveRealtimeUpdates, skip } = options

  const [initialLoadingState, setInitialLoadingState] = useState(FetchRequestState.Loading)
  const [timeline, setTimeline] = useState<Timeline | undefined>()
  const [withChangesOnly, setWithChangesOnly] = useState(false)
  const [eventFilters, setEventFilters] = useState<TimelineEventFilter[]>(initialFilters ?? [])
  const isMounted = useIsMounted()

  const onReceive = useCallback(
    (event: any) => {
      setTimeline((timeline) => {
        if (!timeline) {
          return timeline
        }
        if (layerId && event.layer.id !== layerId) {
          return timeline
        }
        return produce(timeline, (draftState) => {
          draftState.events.unshift({ ...event, isNew: true })
        })
      })
    },
    [layerId],
  )

  useArticleEventsRealtimeChannel(articleKey, receiveRealtimeUpdates, onReceive)

  /*
   * Load the timeline for the first time
   */
  useEffect(() => {
    if (skip) return

    setInitialLoadingState(FetchRequestState.Loading)
    setTimeline(undefined)

    let cancelled = false

    ArticleService.fetchTimelineEvents(
      articleKey,
      userId,
      withChangesOnly,
      client,
      layerId,
      eventFilters,
      timestamp,
    ).then((res) => {
      if (cancelled) {
        return
      }
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        setTimeline(res.data.timeline as Timeline)
        setInitialLoadingState(FetchRequestState.Resolved)
      } else {
        showErrorMessageToast(
          'There was an error getting the timeline for this doc. Our team has been notified and we are looking into it.',
        )
        reportError('Fetching activity timeline failed', { severity: 'error' })
        setInitialLoadingState(FetchRequestState.Rejected)
      }
    })

    return () => {
      cancelled = true
    }
  }, [articleKey, withChangesOnly, userId, skip, client, layerId, eventFilters, timestamp])

  const loadMore = async () => {
    if (!timeline) {
      return
    }
    const response = await client.fetchQuery(
      ['timeline', articleKey, layerId, { timestamp: timeline.oldest_timestamp, types: eventFilters }],
      () =>
        ArticleClient.getTimelineEvents(
          articleKey,
          timeline.oldest_timestamp,
          userId,
          withChangesOnly,
          layerId,
          eventFilters,
        ),
      {
        staleTime: 0,
      },
    )
    if (response.type === SUCCESS_RESPONSE_TYPE) {
      const { events, oldest_timestamp, more_available } = response.data.timeline
      isMounted.current &&
        setTimeline({
          ...timeline,
          events: timeline.events.concat(events as TimelineEvent[]),
          oldest_timestamp,
          more_available,
        })
    }
    return response
  }

  useEffect(() => {
    if (timeline && timeline.events.length < MIN_EVENTS_FIRST_PAGE_SIZE && timeline.more_available) {
      loadMore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, eventFilters])

  return {
    timeline,
    loadMore,
    initialLoadingState,
    withChangesOnly,
    setWithChangesOnly,
    setEventFilters,
    eventFilters,
  }
}
